import * as Select from '@radix-ui/react-select';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { DropdownMenu } from '~/chords/DropdownSingleSelect/components/DropdownMenu';
import { DropdownTrigger } from '~/chords/DropdownSingleSelect/components/DropdownTrigger';
import { useTriggerZIndex } from '~/chords/DropdownSingleSelect/hooks/useTriggerZIndex';
import type {
  DropdownContentOptions,
  DropdownSearchOptions,
  DropdownState,
  ExternalDropdownOptions,
  InternalDropdownOption,
  InternalDropdownOptions,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/chords/DropdownSingleSelect/types';
import { updateExternalToInternal } from '~/chords/DropdownSingleSelect/utils/updateExternalToInternal';
import { updateIsVisibleSearch } from '~/chords/DropdownSingleSelect/utils/updateIsVisibleSearch';
import { updateIsVisibleTrue } from '~/chords/DropdownSingleSelect/utils/updateIsVisibleTrue';
import { updateSelectedOptions } from '~/chords/DropdownSingleSelect/utils/updateSelectedOptions';

export interface DropdownSingleSelectProps<TOptionProps = unknown>
  extends Select.SelectProps {
  id?: string;
  placeholder?: string;
  options: ExternalDropdownOptions<TOptionProps>;
  contentOptions?: DropdownContentOptions;
  searchOptions?: DropdownSearchOptions;
  showCheckIcon?: boolean;
  state?: DropdownState;
  // When true, ignores close requests resulting from blur (e.g. switching tabs).
  // Other closes (Escape, selection, click outside) still apply.
  preventCloseOnWindowBlur?: boolean;
  // Provide an optional React component to show options with custom styling.
  // For instance, display the country flag next to the country name.
  OptionTemplate?: OptionTemplateComponent;
}

/**
 * DropdownSingleSelect is built on top of Radix UI's Select and Scroll Area primitives.
 * https://www.radix-ui.com/docs/primitives/components/select
 * https://www.radix-ui.com/docs/primitives/components/scroll-area
 */
export function DropdownSingleSelect<TOptionProps = unknown>({
  id,
  placeholder,
  options: externalOptions,
  contentOptions,
  searchOptions,
  showCheckIcon,
  state,
  OptionTemplate,
  disabled,
  required,
  onOpenChange: parentOnOpenChange,
  preventCloseOnWindowBlur,
  ...select
}: DropdownSingleSelectProps<TOptionProps>) {
  const initialSelectedValue = select?.value ?? select?.defaultValue;
  const [initialResult] = useState(() =>
    updateExternalToInternal(externalOptions, {
      selectedValue: initialSelectedValue,
    }),
  );

  const [selectedOption, setSelectedOption] = useState<
    InternalDropdownOption | undefined
  >(initialResult.selectedOption);

  const [options, setOptions] = useState<InternalDropdownOptions>(
    initialResult.internalOptions,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const searchQueryTrimmed = searchQuery.trim();
  const searchQueryRef = useRef(searchQuery);
  const [isOpen, setIsOpen] = useState(
    select?.open || select?.defaultOpen || false,
  );
  const isOpenRef = useRef(isOpen);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerRefCallback, triggerZIndex] = useTriggerZIndex(
    triggerRef,
    contentOptions?.zIndex,
  );
  const [selectedValue, setSelectedValue] = useState(initialSelectedValue);

  /**
   * Keep selectedValue in sync with select.value and select.defaultValue.
   */
  useLayoutEffect(() => {
    setSelectedValue(select?.value ?? select?.defaultValue);
  }, [select?.value, select?.defaultValue]);

  /**
   * When the dropdown is closed, clear the search query.
   */
  useEffect(() => {
    if (!isOpen && isOpenRef.current && searchQueryTrimmed) {
      setSearchQuery('');
    }
  }, [isOpen, searchQueryTrimmed]);

  /**
   * Remember the previous isOpen state so that we're only running certain useEffects
   * when isOpen changes and not when any of the other dependencies change.
   */
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  /**
   * Translate the barebones external options object and augment it with the data structure we use
   * internally to manage the dropdown state (options, selection, and visibility).
   */
  const externalDerived = useMemo(
    () => updateExternalToInternal(externalOptions, { selectedValue }),
    [externalOptions, selectedValue],
  );

  const prevExternalDerivedRef = useRef(externalDerived);

  if (externalDerived !== prevExternalDerivedRef.current) {
    prevExternalDerivedRef.current = externalDerived;
    setOptions(externalDerived.internalOptions);
    setSelectedOption(externalDerived.selectedOption);
  }

  /**
   * When the searchQuery changes, update the options to show only the options
   * that match the search query. Skip if internal filtering is disabled.
   */
  useEffect(() => {
    if (searchOptions?.disableInternalFiltering) {
      return;
    }

    // If the search query hasn't changed, do nothing.
    if (searchQueryTrimmed === searchQueryRef.current) {
      return;
    }

    if (searchQueryTrimmed) {
      setOptions((prevOptions) =>
        updateIsVisibleSearch(prevOptions, {
          searchQueryTrimmed,
        }),
      );

      return;
    }

    setOptions(updateIsVisibleTrue);
  }, [searchQueryTrimmed, searchOptions?.disableInternalFiltering]);

  /**
   * Remember the previous searchQuery value so that we're only running certain useEffects
   * when the searchQuery changes.
   */
  useEffect(() => {
    searchQueryRef.current = searchQueryTrimmed;
  }, [searchQueryTrimmed]);

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    searchOptions?.onChange?.(query);
  };

  const handleSelectOption = (newSelectedOption: InternalDropdownOption) => {
    setSelectedOption((prevSelectedOption) => {
      // If this option is already the selected option, do nothing.
      if (prevSelectedOption?.value === newSelectedOption.value) {
        return prevSelectedOption;
      }

      // Update options based on previous selected option.
      setOptions((prevOptions) =>
        updateSelectedOptions(prevOptions, {
          prevSelectedOptionIndex: prevSelectedOption?.optionIndex,
          newSelectedOptionIndex: newSelectedOption.optionIndex,
        }),
      );

      return newSelectedOption;
    });
  };

  const radixWithSelect: RadixUiProps['radix'] = {};

  return (
    <Select.Root
      {...select}
      open={isOpen}
      onOpenChange={(newIsOpen) => {
        if (!newIsOpen && preventCloseOnWindowBlur && !document.hasFocus()) {
          return;
        }

        setIsOpen(newIsOpen);
        parentOnOpenChange?.(newIsOpen);
      }}
      disabled={disabled}
      required={required}
    >
      <DropdownTrigger
        ref={triggerRefCallback}
        id={id}
        placeholder={placeholder}
        selectedOption={selectedOption}
        isOpen={isOpen}
        state={state}
        disabled={disabled}
        OptionTemplate={OptionTemplate}
        radix={radixWithSelect}
      />
      {isOpen ? (
        <DropdownMenu
          options={options}
          contentOptions={{
            ...contentOptions,
            zIndex: triggerZIndex,
          }}
          searchOptions={searchOptions}
          searchQuery={searchQuery}
          onSearchQueryChange={handleSearchQueryChange}
          selectedOption={selectedOption}
          onSelectOption={handleSelectOption}
          showCheckIcon={showCheckIcon}
          OptionTemplate={OptionTemplate}
          radix={radixWithSelect}
        />
      ) : null}
    </Select.Root>
  );
}

DropdownSingleSelect.displayName = 'DropdownSingleSelect';
